import { Matrix } from './matrix';
import { Rect } from './rect';

///////////////////////////////////////////////////////////////////////////////
// Generic operations on 2D vectors.
///////////////////////////////////////////////////////////////////////////////

/** A 2D vector, representing either a point or a direction. */
export interface Vec2D {
  x: number;
  y: number;
}

export function add(v1: Vec2D, v2: Vec2D): Vec2D {
  return { x: v1.x + v2.x, y: v1.y + v2.y };
}

export function subtract(v1: Vec2D, v2: Vec2D): Vec2D {
  return { x: v1.x - v2.x, y: v1.y - v2.y };
}

export function scale(v: Vec2D, alpha: number): Vec2D {
  return { x: v.x * alpha, y: v.y * alpha };
}

/**
 * Scale by the inverse of alpha, returning [0, 0] if alpha is 0. Useful to
 * avoid unexpected NaNs in edge cases.
 */
export function scaleByInverse(v: Vec2D, alpha: number): Vec2D {
  if (alpha === 0) return { x: 0, y: 0 };
  return { x: v.x / alpha, y: v.y / alpha };
}

/** Given an alpha in [0, 1], returns a convex combination of two vectors. */
export function blend(alpha: number, v1: Vec2D, v2: Vec2D): Vec2D {
  const beta = 1 - alpha;
  return {
    x: beta * v1.x + alpha * v2.x,
    y: beta * v1.y + alpha * v2.y,
  };
}

export function dot(v1: Vec2D, v2: Vec2D): number {
  return v1.x * v2.x + v1.y * v2.y;
}

export function magnitude(v: Vec2D): number {
  return Math.sqrt(dot(v, v));
}

///////////////////////////////////////////////////////////////////////////////
// Operations on 2D points.
///////////////////////////////////////////////////////////////////////////////

/** A 2D point. */
export type Point = Vec2D;

export function roundPoint(p: Point): Point {
  return {
    x: Math.round(p.x),
    y: Math.round(p.y),
  };
}

export function distance(p1: Point, p2: Point): number {
  return magnitude(subtract(p1, p2));
}

export function manhattanDistance(p1: Point, p2: Point): number {
  return Math.abs(p1.x - p2.x) + Math.abs(p1.y - p2.y);
}

///////////////////////////////////////////////////////////////////////////////
// Operations on 2D axis-aligned rectangles. ("Rects" for short.)
///////////////////////////////////////////////////////////////////////////////

export function makeRect(left: number, top: number, width: number, height: number): Rect {
  return new Rect(left, top, width, height);
}

export function makeRectAt(p: Point, width: number, height: number): Rect {
  return makeRect(p.x, p.y, width, height);
}

export function makeRectCenteredAt(p: Point, width: number, height: number): Rect {
  return makeRect(p.x - width / 2, p.y - height / 2, width, height);
}

export function emptyRect(): Rect {
  return new Rect();
}

export function topLeft(r: Rect): Point {
  return { x: r.left, y: r.top };
}

export function topRight(r: Rect): Point {
  return { x: r.right, y: r.top };
}

export function bottomLeft(r: Rect): Point {
  return { x: r.left, y: r.bottom };
}

export function bottomRight(r: Rect): Point {
  return { x: r.right, y: r.bottom };
}

export function center(r: Rect): Point {
  return { x: r.left + r.width / 2, y: r.top + r.height / 2 };
}

export function isEmptyRect(r: Rect): boolean {
  return r.width <= 0 || r.height <= 0;
}

/**
 * @returns true if r1 and r2's intersection is non-empty. Empty rects don't
 * intersect with anything, even themselves. (Note that this is usually what you
 * want, but not always!)
 */
export function rectsIntersect(r1: Rect, r2: Rect): boolean {
  if (isEmptyRect(r1) || isEmptyRect(r2)) {
    return false;
  }
  if (r1.right < r2.left) {
    return false;
  }
  if (r1.left > r2.right) {
    return false;
  }
  if (r1.bottom < r2.top) {
    return false;
  }
  if (r1.top > r2.bottom) {
    return false;
  }
  return true;
}

/**
 * @returns true if r1 and r2 are equivalent. All empty rects are considered
 * equal.
 */
export function rectsAreEqual(r1: Rect, r2: Rect): boolean {
  if (isEmptyRect(r1)) {
    return isEmptyRect(r2);
  }
  if (isEmptyRect(r2)) {
    return false;
  }
  return (
    r1.left === r2.left && r1.top === r2.top && r1.right === r2.right && r1.bottom === r2.bottom
  );
}

/** @returns the smallest rect which includes both r1 and r2. */
export function unionRects(r1: Rect, r2: Rect): Rect {
  if (isEmptyRect(r2)) {
    return r1;
  }
  if (isEmptyRect(r1)) {
    return r2;
  }
  if (r1.left <= r2.left && r1.right >= r2.right && r1.top <= r2.top && r1.bottom >= r2.bottom) {
    return r1;
  }
  const left = Math.min(r1.left, r2.left);
  const top = Math.min(r1.top, r2.top);
  const right = Math.max(r1.right, r2.right);
  const bottom = Math.max(r1.bottom, r2.bottom);
  const width = right - left;
  const height = bottom - top;
  return new Rect(left, top, width, height);
}

/** @returns the smallest rect which all of the provided rects. */
export function unionAllRects(rects: Rect[]): Rect {
  if (rects.length < 1) {
    return new Rect();
  }
  return rects.reduce(unionRects);
}

/**
 * @returns the rect formed by the intersection of r1 and r2, or an empty rect
 * if their intersection is empty.
 */
export function intersectRects(r1: Rect, r2: Rect): Rect {
  if (!rectsIntersect(r1, r2)) {
    return new Rect();
  }
  const left = Math.max(r1.left, r2.left);
  const top = Math.max(r1.top, r2.top);
  const right = Math.min(r1.right, r2.right);
  const bottom = Math.min(r1.bottom, r2.bottom);
  const width = right - left;
  const height = bottom - top;
  return new Rect(left, top, width, height);
}

/**
 * @returns the rect formed by the intersection of r1 and r2, or an empty rect
 * if their intersection is empty.
 */
export function intersectAllRects(rects: Rect[]): Rect {
  if (rects.length < 1) {
    return new Rect();
  }
  return rects.reduce(intersectRects);
}

/**
 * Rounds r to integer coordinates in such a way that it contains strictly more
 * points than it would have otherwise.
 */
export function roundOut(r: Rect): Rect {
  const left = Math.floor(r.left);
  const top = Math.floor(r.top);
  const right = Math.ceil(r.right);
  const bottom = Math.ceil(r.bottom);
  const width = right - left;
  const height = bottom - top;
  return new Rect(left, top, width, height);
}

/**
 * @returns a new rect with each side moved toward the center by the given
 * number of units. If only the left inset is specified, the top, right, and
 * bottom insets are set to the same value. If only the left and top insets are
 * specified, the right and bottom insets are set to the same values.
 *
 * If the given insets would cause the new rect's size to become negative or
 * zero, it's returned unchanged. That's usually a reasonable fallback; the
 * caller can check for this if they need different handling.
 */
export function insetBy(
  r: Rect,
  left: number,
  top: number = left,
  right: number = left,
  bottom: number = top,
): Rect {
  const widthReduction = left + right;
  const heightReduction = top + bottom;
  if (widthReduction >= r.width || heightReduction >= r.height) {
    return r;
  }
  return new Rect(r.left + left, r.top + top, r.width - widthReduction, r.height - heightReduction);
}

/**
 * @returns a new rect with each side moved away from the center by the given
 * number of units. If only the left outset is specified, the top, right, and
 * bottom outsets are set to the same value. If only the left and top outsets
 * are specified, the right and bottom outsets are set to the same values.
 *
 * If the given outsets would cause the new rect's size to become negative or
 * zero, it's returned unchanged. That's usually a reasonable fallback; the
 * caller can check for this if they need different handling.
 */
export function outsetBy(
  r: Rect,
  left: number,
  top: number = left,
  right: number = left,
  bottom: number = top,
): Rect {
  return insetBy(r, -left, -top, -right, -bottom);
}

/** @returns the aspect ratio of r: width / height. If r is empty, returns 1. */
export function aspectRatio(r: Rect): number {
  if (isEmptyRect(r)) {
    return 1;
  }
  return r.width / r.height;
}

/**
 * @returns the smallest rect which contains r and has the given aspect ratio.
 * Useful when you need to "zoom into" some element within a larger viewport; use
 * growToAspectRatio(elementBoundingBox, aspectRatio(viewport)) to calculate the
 * area of the viewport which should be shown, and then compare the result with
 * the original viewport to calculate the necessary translation and scale.
 */
export function growToAspectRatio(r: Rect, newRatio: number): Rect {
  if (newRatio === 0 || r.width === 0 || r.height === 0) {
    return r;
  }

  const currentRatio = aspectRatio(r);
  if (currentRatio > newRatio) {
    // Increase height to match the new aspect ratio.
    const newHeight = r.width / newRatio;
    const heightOutset = (newHeight - r.height) / 2;
    return outsetBy(r, 0, heightOutset);
  } else {
    // Increase width to match the new aspect ratio.
    const newWidth = r.height * newRatio;
    const widthOutset = (newWidth - r.width) / 2;
    return outsetBy(r, widthOutset, 0);
  }
}

export function translateRectBy(r: Rect, x: number, y: number): Rect {
  if (x === 0 && y === 0) {
    return r;
  }
  return new Rect(r.left + x, r.top + y, r.width, r.height);
}

export function translateRect(r: Rect, v: Vec2D): Rect {
  return translateRectBy(r, v.x, v.y);
}

/**
 * @returns a scaled version of r. The position is scaled as well as the size -
 * in other words, this represents a coordinate system change.
 */
export function scaleRectBy(r: Rect, s: number): Rect {
  return new Rect(r.left * s, r.top * s, r.width * s, r.height * s);
}

/**
 * Scale a rect by the inverse of s, returning an empty rect if s is 0. Useful
 * to avoid unexpected NaNs in edge cases.
 */
export function scaleRectByInverse(r: Rect, s: number): Rect {
  if (s === 0) {
    return new Rect();
  }
  return scaleRectBy(r, 1 / s);
}

/**
 * @returns the point on the rect r which is nearest to p. If p is on or inside
 * r, the nearest point is p itself.
 */
export function nearestPointOn(r: Rect, p: Point): Point {
  return {
    x: Math.min(Math.max(p.x, r.left), r.right),
    y: Math.min(Math.max(p.y, r.top), r.bottom),
  };
}

/**
 * @returns the distance from a 1D point p to a 1D line segment [lo, hi]. The
 * distance is zero if p lies on the segment.
 */
function distToLineSegment1D(p: number, lo: number, hi: number): number {
  if (p < lo) {
    return lo - p;
  } else if (p <= hi) {
    return 0;
  } else {
    return hi - p;
  }
}

/**
 * @returns the Manhattan distance from p to the nearest point on the rect r. If
 * p is on or inside r, the distance is zero.
 */
export function manhattanDistanceToRect(r: Rect, p: Point): number {
  return (
    Math.abs(distToLineSegment1D(p.x, r.left, r.right)) +
    Math.abs(distToLineSegment1D(p.y, r.top, r.bottom))
  );
}

/**
 * @returns the rect r transformed by the matrix m, if m has no rotation or skew
 * component; otherwise, returns the smallest axis-aligned rect that contains
 * the resulting shape. This limitation is necessary because Rects can only
 * represent axis-aligned rectangles.
 */
export function transformRect(m: Matrix | DOMMatrixReadOnly, r: Rect): Rect {
  const p1 = m.transformPoint({ x: r.left, y: r.top });
  const p2 = m.transformPoint({ x: r.right, y: r.bottom });
  const left = Math.min(p1.x, p2.x);
  const top = Math.min(p1.y, p2.y);
  const right = Math.max(p1.x, p2.x);
  const bottom = Math.max(p1.y, p2.y);
  const width = right - left;
  const height = bottom - top;
  return new Rect(left, top, width, height);
}
